import axios from 'axios';
import * as _ from 'lodash';
import { store } from '../redux/store';
import { authActions } from '../redux/auth/authSlice';
import { createNewAccessToken } from './authApi';
import { Platform } from '../utils/enums/platform-type.enum';
import { modelAction } from '../redux/models/modelsSlice';

let isLoggingOut = false; // Flag to prevent duplicate logout calls

const createAxiosInstance = () => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_CLOUD_URL,
    withCredentials: true,
    headers: {
      platform: Platform.SAAS,
    },
  });

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const initialRequest = error.config;

      // Skip interceptor for login requests to avoid interference
      if (error.request?.responseURL.includes('auth/login')) {
        return Promise.reject(error);
      }

      // Handle token refresh failures and retry failures
      if (
        (error.response &&
          (error.response.status === 401 || error.response.status === 500) &&
          error.request?.responseURL.includes('auth/access-token')) ||
        (error.response && error.response.status === 401 && initialRequest._retry)
      ) {
        // Prevent duplicate logout calls
        if (!isLoggingOut) {
          isLoggingOut = true;
          store.dispatch(authActions.logOutAsync());
          // Reset flag after a delay to allow for proper logout processing
          setTimeout(() => {
            isLoggingOut = false;
          }, 1000);
        }
        return Promise.reject(error);
      }

      // Check if the response status code is 401 (unauthorized) for other requests
      if (
        error.response &&
        error.response.status === 401 &&
        !error.request?.responseURL.includes('auth/access-token') &&
        !initialRequest._retry
      ) {
        initialRequest._retry = true; // Mark the request as already retried

        try {
          // Create new access token
          await createNewAccessToken();

          // Retry the original request with the new access token
          return instance(initialRequest);
        } catch (refreshError) {
          // Handle the error when refreshing the token fails
          const refreshErrorStatus = _.get(refreshError, 'response.status');
          if (refreshErrorStatus === 400 || refreshErrorStatus === 500) {
            // Prevent duplicate logout calls
            if (!isLoggingOut) {
              isLoggingOut = true;
              store.dispatch(authActions.logOutAsync());
              setTimeout(() => {
                isLoggingOut = false;
              }, 1000);
            }
          }
          return Promise.reject(refreshError);
        }
      }

      if (error.response && error.response.status === 403) {
        store.dispatch(modelAction.setIsPermissionDeniedModelOpen(true));
      }

      // Return any other errors
      return Promise.reject(error);
    },
  );

  return instance;
};

export const backendApiInstance = createAxiosInstance();
